import formActions from 'redux-form/lib/actions'
import getFormValues from 'redux-form/lib/getFormValues'
import mapKeys from 'lodash/mapKeys'
import snakeCase from 'lodash/snakeCase'
import {call, put, select} from 'redux-saga/effects'

import * as formHelpers from '../../components/Forms/helpers'
import * as userSelectors from '../../store/user'
import {
  denormalizeFormValues,
  formatAddressValues,
  prependFormName,
} from '../../reducers/checkout/helpers'
import {initialState} from '../../reducers/checkout'
import {saveBillingAddress} from '../../api/checkout'
import {BAD_REQUEST, isBadResponse} from '../../api/statusCodes'
import {checkoutApiError, SAVE_BILLING} from '../../checkoutErrorCodes'
import {renameKeys} from '../../utils'

/**
 * Formats billing address form data stored in Redux into the expected format for the API payload.
 *
 * From:
 * {
 *   billing: {
 *     address: {
 *       zip: '12345'
 *     }
 *   }
 * }
 *
 * To:
 * {
 *   billing_zip: '1234
 * }
 *
 * @param billingFormValues - Stored redux-form values for the billing address form
 * @returns {Object} - Payload to send for saving shipping address
 */
export const formatBillingAddressPayload = (billingFormValues) => {
  return {
    ...mapKeys(initialState.billingAddress, (_, key) => prependFormName('billing', snakeCase(key))),
    ...denormalizeFormValues('billing', billingFormValues),
  }
}

export function* getSaveBillingAddressPayload(formName) {
  const userId = yield select(userSelectors.id)
  const formValues = yield select(getFormValues(formName))
  const formattedBillingAddress = formatBillingAddressPayload(formValues)
  return [userId, formattedBillingAddress]
}

export function* saveBillingInformationFromReduxForm(formName) {
  const billingAddressPayload = yield call(getSaveBillingAddressPayload, formName)
  const {json, status} = yield call(saveBillingAddress, ...billingAddressPayload)

  // check if there are validation checks that failed on the address fields
  if (status === BAD_REQUEST) {
    yield put(
      formActions.stopSubmit(formName, {
        billing: {
          address: formatAddressValues('billing', json),
        },
      }),
    )
    throw new Error('Unable to save billing information.')
  }

  // check if we get a non-OK status
  if (isBadResponse(status)) {
    throw checkoutApiError(SAVE_BILLING, status)
  }

  return json
}

export const formatShippingAddressPayloadForServer = (payload) =>
  renameKeys(payload, {
    [formHelpers.FIRST_NAME_FIELD]: 'shipping_first_name',
    [formHelpers.LAST_NAME_FIELD]: 'shipping_last_name',
    [formHelpers.ADDRESS_1_FIELD]: 'shipping_address_1',
    [formHelpers.ADDRESS_2_FIELD]: 'shipping_address_2',
    [formHelpers.CITY_FIELD]: 'shipping_city',
    [formHelpers.STATE_FIELD]: 'shipping_state',
    [formHelpers.ZIP_FIELD]: 'shipping_zip',
    [formHelpers.PHONE_FIELD]: 'shipping_phone',
    [formHelpers.TO_BUSINESS_ADDRESS_FIELD]: 'shipping_to_business_address',
    ignoredVerifiedAddress: 'ignored_verified_address',
  })

export const formatShippingAddressPayloadForClient = (payload) =>
  renameKeys(payload, {
    shipping_first_name: formHelpers.FIRST_NAME_FIELD,
    shipping_last_name: formHelpers.LAST_NAME_FIELD,
    shipping_address_1: formHelpers.ADDRESS_1_FIELD,
    shipping_address_2: formHelpers.ADDRESS_2_FIELD,
    shipping_city: formHelpers.CITY_FIELD,
    shipping_state: formHelpers.STATE_FIELD,
    shipping_zip: formHelpers.ZIP_FIELD,
    shipping_phone: formHelpers.PHONE_FIELD,
    shipping_to_business_address: formHelpers.TO_BUSINESS_ADDRESS_FIELD,
  })
